import { useState, useEffect, useRef } from 'react'
import { saveDivisionPicks, loadDivisionPicks, getDayKey } from '../lib/db'
import { attemptFreezeSnapshot } from '../lib/odds'
import { flagUrl, playerInitials } from '../lib/golfCountryFlag'
import { log } from '../lib/log'

// Tier-label table for the body-render tier headers (used near line 401
// in the unlocked roster render). Kept at module scope so the freemium
// dispatcher gate in App.jsx can re-export or share it later for a
// locked+frozen tier preview without duplicating the constant.
const TIER_LABELS_BY_KEY = {
  '1': 'T1 · ranks 1-10',
  '2': 'T2 · ranks 11-20',
  '3': 'T3 · ranks 21-30',
  '4': 'T4 · ranks 31-40',
  '5': 'T5 · ranks 41-50',
  '6': 'T6 · rank 51 → end of field',
}

// ── Phase 3.5 deferred-snapshot freeze-on-mount throttle ───────────
// Module-scope per-session memory: groupId → last-attempt timestamp ms.
// Prevents an idle awaiting-odds pool from dripping the rate-limited
// DataGolf API on every picker remount (operator refinement 1).
// Page refresh clears it. "Check now" explicit button bypasses by
// resetting the entry to 0 before re-attempting.
const FREEZE_THROTTLE_MS    = 3 * 60 * 1000   // 3 minutes
const lastFreezeAttemptByGroup = new Map()    // groupId → ms

// Status derivation with Phase 3 legacy compat. A Phase 3 pool created
// before commit 1675d57 has tierSnapshot but no snapshotStatus field —
// treat as 'frozen' so the picker renders the live roster without
// needing a migration of golf_config.
function deriveSnapshotStatus(golfConfig) {
  const explicit = golfConfig?.snapshotStatus
  if (explicit) return explicit
  if (golfConfig?.tierSnapshot) return 'frozen'
  return 'awaiting_odds'
}

// Golf divisions mode — roster-style picker (Pick-6 / Use-4). Distinct
// UX from GolfPicksMode (matchup/pair picker). The user selects one
// golfer per tier across 6 tiers (T1-T5 = 10 golfers each, T6 = rank
// 51 to end of field). Final entry = 6 golfers; Phase 4 grader scores
// the best 4 of 6.
//
// DATA SOURCE — golf_config (frozen at pool creation)
//
// The picker reads everything it needs from `group.golf_config`:
//   - eventId           : the DataGolf event id
//   - tierSnapshot      : { "1": [dgIds...], ..., "6": [dgIds...] } —
//                         the immutable source of truth for who is in
//                         each tier (frozen at pool creation via the
//                         server's /api/golf/divisions/tier-snapshot
//                         endpoint; see Phase 3 commit 1 + commit 3).
//   - tierPlayerLookup  : { dgId: { name, country, winProb, dgRank } }
//                         — denormalized display metadata frozen
//                         alongside tierSnapshot at pool creation.
//                         Lets the picker render names + odds without
//                         re-fetching DataGolf (which may have flipped
//                         to a future event by the time the picker
//                         loads, especially mid-/post-event).
//
// SAVE-TIME VALIDATION
//
// saveDivisionPicks (src/lib/db.js) validates the entry client-side
// against tierSnapshot[tier] before the upsert. Server-side enforcement
// via RPC is the documented Phase 4 / hardening gap (see HANDOFF
// design-question section + saveDivisionPicks docblock).
//
// SCORE-TO-PAR TIEBREAKER (Phase 3 collect-only)
//
// The "what's the winning score (to par)?" number input writes to
// group_members.score_to_par_guess. Phase 3 stores it ONLY — no grader
// integration, no tiebreak computation. Phase 4 wires it into the
// finalize/tiebreak path.
//
// LAYOUT — mirrors GolfPicksMode's layered structure
//
//   • Outer div   : position:relative, no z-index
//   • Bg layer    : position:fixed inset:0 zIndex:0 pointer-events:none
//                   (sunset-course image with gradient scrim — same
//                    asset as the matchup picker by default; the
//                    persistent-UI wrapper in App.jsx covers it where
//                    the scoreboard/podium/tabs sit)
//   • Content    : wrapped in position:relative zIndex:1
//   • Sticky-submit + toast : UNWRAPPED siblings of content wrapper,
//                              z:90 / z:100 at root context — same
//                              trade-off documented in GolfPicksMode
//                              (preserving bottom-nav stacking).

export default function GolfDivisionsMode({
  group, user, members, onPointsUpdate, gradeKey = 0, promptAuth = () => {},
  // Pattern #2 freemium tease (divisions prototype). When `locked` is true,
  // the picker UI is fully interactive but handleSubmit intercepts to
  // onUnlockRequest() instead of calling saveDivisionPicks — picks live in
  // local React state only (ephemeral; no localStorage/server write until
  // the pool is unlocked). The sticky-submit at the bottom is replaced by
  // a sticky unlock bar that triggers the unlock checkout flow. When
  // `locked` is false (default), behavior is byte-identical to pre-Pattern-#2.
  locked = false,
  onUnlockRequest,
  unlocking = false,
  unlockPriceCents = null,
}) {
  const eventId         = group?.golf_config?.eventId
  const eventStartDate  = group?.golf_config?.eventStartDate || null

  // ── Phase 3.5: liveGolfConfig tracks the picker's current view of
  //    golf_config across the awaiting → frozen transition. Initial
  //    value comes from the group prop; on a successful lazy-freeze
  //    we update from the RPC's returned config WITHOUT waiting for a
  //    parent getUserGroups refresh, so the picker flips into roster
  //    mode immediately.
  const [liveGolfConfig, setLiveGolfConfig] = useState(group?.golf_config || {})
  useEffect(() => {
    // Sync if the parent reloads with a fresh golf_config — but only
    // when the new one carries MORE info than what we have (avoids
    // clobbering an in-session freeze if the parent's getUserGroups
    // returns the pre-freeze copy because of a cache lag).
    const next = group?.golf_config
    if (!next) return
    const localStatus = deriveSnapshotStatus(liveGolfConfig)
    const nextStatus  = deriveSnapshotStatus(next)
    if (localStatus === 'awaiting_odds' && nextStatus === 'frozen') setLiveGolfConfig(next)
    if (localStatus === nextStatus && JSON.stringify(next) !== JSON.stringify(liveGolfConfig)) {
      // Same status, but other golf_config keys may have changed; sync.
      setLiveGolfConfig(next)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [group?.golf_config])

  // Status drives the render branch. Initial render reflects whatever
  // came in via the prop; subsequent renders reflect the post-freeze
  // state once attemptFreezeSnapshot returns 'frozen'.
  const snapshotStatus  = deriveSnapshotStatus(liveGolfConfig)
  const tierSnapshot    = liveGolfConfig?.tierSnapshot    || null
  const tierPlayerLookup= liveGolfConfig?.tierPlayerLookup || {}
  const eventName       = liveGolfConfig?.eventName        || ''

  // Tournament-started variant: awaiting_odds AND eventStartDate is
  // today or past. Pool never received a snapshot in time; show the
  // "no picks could be made" friendly explanation.
  const todayCal           = getDayKey()
  const tournamentStarted  = (eventStartDate && eventStartDate <= todayCal)
  const isAwaiting         = snapshotStatus === 'awaiting_odds'
  const isFrozen           = snapshotStatus === 'frozen'

  // ── Freeze-on-mount (Phase 3.5, throttled) ─────────────────────
  // Triggers a single attemptFreezeSnapshot per mount for awaiting-odds
  // pools, throttled per-group to avoid drip-feeding DataGolf on every
  // remount of an idle awaiting pool. The "Check now" button bypasses
  // by zeroing the throttle entry before re-attempting.
  const [freezeAttempting, setFreezeAttempting]   = useState(false)
  const [freezeLastError,  setFreezeLastError]    = useState(null) // {code, detail}
  const [freezeManualHint, setFreezeManualHint]   = useState(null) // ephemeral feedback

  async function runFreezeAttempt({ bypassThrottle = false } = {}) {
    if (!group?.id) return
    if (tournamentStarted) return     // pointless — no picks possible
    if (freezeAttempting) return       // single-flight
    const last = lastFreezeAttemptByGroup.get(group.id) || 0
    if (!bypassThrottle && (Date.now() - last < FREEZE_THROTTLE_MS)) return
    lastFreezeAttemptByGroup.set(group.id, Date.now())
    setFreezeAttempting(true)
    setFreezeLastError(null)
    try {
      const result = await attemptFreezeSnapshot(group.id)
      if (result.ok) {
        if (result.golfConfig) setLiveGolfConfig(result.golfConfig)
        if (result.status === 'frozen') {
          setFreezeManualHint(null)
        } else if (result.status === 'still_awaiting') {
          // Odds still not out; surface the underlying reason for the
          // manual-retry feedback area below the placeholder.
          setFreezeLastError(result.error || { code: 'STILL_AWAITING', detail: 'Odds not available yet' })
          if (bypassThrottle) setFreezeManualHint('Checked just now — odds still aren\'t out.')
        }
      } else {
        setFreezeLastError(result.error || { code: 'UNKNOWN', detail: 'Freeze attempt failed' })
        if (bypassThrottle) {
          setFreezeManualHint(`Couldn't check (${result.error?.code || 'error'}). Try again.`)
        }
      }
    } catch (err) {
      log('[divisions] freeze attempt threw:', err?.message || err)
      setFreezeLastError({ code: 'NETWORK_FAILED', detail: err?.message || 'fetch failed' })
    } finally {
      setFreezeAttempting(false)
    }
  }

  useEffect(() => {
    if (!isAwaiting)         return
    if (tournamentStarted)   return
    runFreezeAttempt({ bypassThrottle: false })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [group?.id, isAwaiting, tournamentStarted])

  function handleCheckNow() {
    setFreezeManualHint('Checking…')
    runFreezeAttempt({ bypassThrottle: true })
  }

  // picks: { "<tier>": { tier, selectedDgId, selectedName } }
  const [picks,    setPicks]    = useState({})
  const [tieGuess, setTieGuess] = useState('')   // string for input control; coerced on save
  const [submitted, setSubmitted] = useState(false)
  const [loading, setLoading]   = useState(true)
  const [toast,   setToast]     = useState(null)
  const toastTimerRef = useRef(null)

  function showToast(msg) {
    setToast(msg)
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current)
    toastTimerRef.current = setTimeout(() => setToast(null), 2500)
  }

  // ── Init: load any existing entry (in-progress draft from localStorage
  //    OR a previously-submitted entry from golf_division_picks).
  useEffect(() => {
    if (!group?.id || !user?.id || !eventId) {
      setLoading(false)
      return
    }
    let cancelled = false
    ;(async () => {
      setLoading(true)
      try {
        const bundle = await loadDivisionPicks(group.id, user.id, eventId)
        if (cancelled) return
        if (bundle?.picks) setPicks(bundle.picks)
        if (bundle?.tieGuess != null) setTieGuess(String(bundle.tieGuess))
        // Submitted = any picks have a non-pending result (Phase 4 marker)
        // OR exactly-6 picks present (entry was previously committed).
        const allPicked = Object.keys(bundle?.picks || {}).length === 6
        if (allPicked) setSubmitted(true)
      } catch (err) {
        log('[divisions] load failed:', err?.message || err)
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
    // gradeKey triggers a reload on the user pulling the picks tab —
    // mirrors GolfPicksMode's gradeKey usage.
  }, [group?.id, user?.id, eventId, gradeKey])

  // ── Auto-mirror in-progress picks to localStorage on every pick change.
  //    Mirrors saveDivisionPicks's localStorage key format. No remote
  //    write until the user submits explicitly. Refresh-survives draft.
  useEffect(() => {
    if (!group?.id || !user?.id || !eventId) return
    try {
      const arr = Object.values(picks).map(p => ({
        tier:         p.tier,
        selectedDgId: p.selectedDgId,
        selectedName: p.selectedName,
      }))
      const tg = tieGuess === '' ? null : parseInt(tieGuess, 10)
      localStorage.setItem(
        `wb_divisionpicks_${group.id}_${user.id}_${eventId}`,
        JSON.stringify({ picks: arr, tieGuess: Number.isFinite(tg) ? tg : null })
      )
    } catch {}
  }, [picks, tieGuess, group?.id, user?.id, eventId])

  // ── Derived state
  const pickedCount  = Object.keys(picks).length
  const tieGuessInt  = (() => {
    if (tieGuess === '' || tieGuess === '-') return null
    const n = parseInt(tieGuess, 10)
    return Number.isFinite(n) ? n : null
  })()
  const allPicked    = pickedCount === 6
  const tieGuessValid= tieGuess === '' || tieGuessInt != null
  // Phase 3: tieGuess is optional but recommended. Submit gate requires
  // 6 picks; tiebreaker can be filled later via re-submit. Once submitted
  // the button hides (canSubmit goes false; user sees "Entry locked in").
  const canSubmit    = !loading && !submitted && allPicked && tieGuessValid

  function handlePick(tier, dgId) {
    if (submitted) return
    const player = tierPlayerLookup[String(dgId)]
    const name   = player?.name || ''
    setPicks(prev => ({
      ...prev,
      [String(tier)]: { tier, selectedDgId: String(dgId), selectedName: name },
    }))
  }

  async function handleSubmit() {
    if (!canSubmit) return
    // Anonymous-browsing gate — saveDivisionPicks needs user.id; if no
    // user, pop the Auth overlay and stop. requireAuth wrapper lives in
    // App.jsx; we receive its trigger as the promptAuth prop.
    if (!user?.id) return promptAuth()
    // Pattern #2 intercept — locked pool: route to unlock checkout
    // instead of writing picks. The 041 RESTRICTIVE policies would
    // reject the saveDivisionPicks INSERT with 42501 anyway, so this
    // is the graceful client-side gate (defense-in-depth: the sticky
    // submit isn't even rendered when locked, but this guard catches
    // any future call-site that triggers handleSubmit directly).
    if (locked) {
      onUnlockRequest?.()
      return
    }
    const arr = Object.values(picks)
    const result = await saveDivisionPicks(
      group.id,
      user.id,
      eventId,
      arr,
      tieGuessInt,
      tierSnapshot,
    )
    if (!result.ok) {
      showToast(`Save failed: ${result.error?.code || 'unknown'} — ${result.error?.detail || ''}`)
      log('[divisions] save failed:', result.error)
      return
    }
    setSubmitted(true)
    showToast('Entry submitted')
  }

  // ── EARLY EXITS ────────────────────────────────────────────────

  // Defensive: pool created with no eventId in golf_config. Shouldn't
  // happen via the create flow, but if it does we surface it.
  if (!eventId) {
    return (
      <div style={{ padding: '40px 20px', textAlign: 'center', color: 'var(--text-dim)', fontFamily: 'inherit' }}>
        <div style={{ fontSize: 40, marginBottom: 12 }}>⚠️</div>
        <div style={{ fontSize: 14, marginBottom: 6 }}>Pool is missing an event id.</div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', fontFamily: 'Arial, sans-serif' }}>
          This pool's golf_config has no eventId — the create flow may have
          glitched. Contact the pool creator.
        </div>
      </div>
    )
  }

  // ── (A) Tournament-started variant ──
  // Awaiting-odds + start_date is today or past → pool never received
  // a snapshot in time. No picks could be made. Graceful explanation.
  if (isAwaiting && tournamentStarted) {
    return (
      <div style={{ padding: '40px 20px', textAlign: 'center', fontFamily: 'inherit' }}>
        <div style={{ fontSize: 40, marginBottom: 12 }}>🏁</div>
        <div style={{ fontSize: 15, color: 'var(--text)', marginBottom: 6, fontWeight: 600 }}>
          Tournament has started without odds being released to this pool.
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', fontFamily: 'Arial, sans-serif', lineHeight: 1.5, maxWidth: 320, margin: '8px auto 0' }}>
          {eventName ? <><strong style={{ color: 'var(--text-dim)' }}>{eventName}</strong> · </> : null}
          Round 1 was {eventStartDate}. No picks could be made for this pool.
          Contact the pool creator about next steps.
        </div>
      </div>
    )
  }

  // ── (B) Awaiting-odds placeholder ──
  // Friendly ⏳ non-error state. Auto-attempt fires once per
  // FREEZE_THROTTLE_MS on mount; "Check now" bypasses for explicit
  // user intent.
  if (isAwaiting) {
    return (
      <div style={{
        padding: '40px 20px',
        textAlign: 'center',
        fontFamily: 'inherit',
        maxWidth: 380,
        margin: '0 auto',
      }}>
        <div style={{ fontSize: 44, marginBottom: 14 }}>⏳</div>
        <div style={{ fontSize: 15, color: 'var(--text)', fontWeight: 600, marginBottom: 8 }}>
          Odds aren't out yet for {eventName || 'this tournament'}.
        </div>
        {eventStartDate && (
          <div style={{ fontSize: 12, color: 'var(--text-dim)', fontFamily: 'Arial, sans-serif', marginBottom: 14 }}>
            Tournament starts {eventStartDate}.
          </div>
        )}
        <div style={{ fontSize: 12, color: 'var(--text-muted)', fontFamily: 'Arial, sans-serif', lineHeight: 1.6, marginBottom: 18 }}>
          Pre-tournament odds typically post a few days before Round 1.
          Check back closer to the tournament — your picks will open
          automatically once the field firms up.
        </div>
        <button
          type="button"
          onClick={handleCheckNow}
          disabled={freezeAttempting}
          className="btn btn-ghost btn-sm"
          style={{
            width: 'auto',
            minWidth: 140,
            opacity: freezeAttempting ? 0.6 : 1,
            cursor: freezeAttempting ? 'wait' : 'pointer',
          }}
        >
          {freezeAttempting ? 'Checking…' : 'Check now'}
        </button>
        {freezeManualHint && (
          <div style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'Arial, sans-serif', marginTop: 10 }}>
            {freezeManualHint}
          </div>
        )}
        {freezeLastError && freezeLastError.code !== 'STILL_AWAITING' && !freezeManualHint && (
          <div style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'Arial, sans-serif', marginTop: 10 }}>
            (Last check: {freezeLastError.code})
          </div>
        )}
      </div>
    )
  }

  // ── (C) Frozen path — fall through to existing roster render ──
  // tierSnapshot must be present at this point (isFrozen branch).
  // Defensive: if somehow we're here without it, fall back to the
  // historical placeholder.
  if (!tierSnapshot) {
    return (
      <div style={{ padding: '40px 20px', textAlign: 'center', color: 'var(--text-dim)', fontFamily: 'inherit' }}>
        <div style={{ fontSize: 40, marginBottom: 12 }}>⏳</div>
        <div style={{ fontSize: 14, marginBottom: 6 }}>Tier snapshot not available.</div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', fontFamily: 'Arial, sans-serif' }}>
          golf_config shape inconsistency — snapshotStatus is 'frozen' but
          tierSnapshot is missing. Contact the pool creator.
        </div>
      </div>
    )
  }
  if (loading) {
    return (
      <div style={{ padding: '40px 20px', textAlign: 'center', color: 'var(--text-dim)' }}>
        Loading Golf Pick 6 picksheet…
      </div>
    )
  }

  // ── RENDER ─────────────────────────────────────────────────────
  // Sourced from the module-scope TIER_LABELS_BY_KEY so the locked
  // tierPreview (§10.4 frozen cell, set at the freemium gate above)
  // can't drift from the unlocked roster headers.
  const tierLabels = TIER_LABELS_BY_KEY

  return (
    <div style={{
      position: 'relative',
      margin: '-16px -16px 0',
      padding: '16px',
      minHeight: 'calc(100vh - 160px)',
    }}>
      {/* Bg layer — pinned to viewport, behind content. Same asset as
          GolfPicksMode for visual continuity; can be swapped later. */}
      <div aria-hidden="true" style={{
        position: 'fixed',
        inset: 0,
        zIndex: 0,
        pointerEvents: 'none',
        background:
          "linear-gradient(180deg, rgba(0,0,0,0.50) 0%, rgba(0,0,0,0.62) 100%), " +
          "url('/sport-bg/golf-picker.webp') center center / cover no-repeat",
      }} />

      {/* In-flow content wrapper — z:1 above bg layer */}
      <div style={{ position: 'relative', zIndex: 1 }}>

        {/* Header — mode summary + progress */}
        <div style={{
          background: 'rgba(8,14,22,0.55)',
          backdropFilter: 'blur(14px)',
          WebkitBackdropFilter: 'blur(14px)',
          border: '1px solid rgba(255,255,255,0.10)',
          borderRadius: 14,
          padding: '12px 14px',
          marginBottom: 12,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)' }}>
                Pick 1 per tier
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-dim)', fontFamily: 'Arial, sans-serif', marginTop: 2 }}>
                Best 4 of 6 count · Lowest score wins
              </div>
            </div>
            <div style={{
              fontSize: 13, fontWeight: 700, color: allPicked ? 'var(--green)' : 'var(--gold)',
              fontFamily: 'inherit',
            }}>
              {pickedCount}/6
            </div>
          </div>
        </div>

        {/* Score-to-par tiebreaker — Phase 3 collect-only, stored on
            group_members.score_to_par_guess; NOT wired into grader
            or tiebreak math. Number input with sign + integer range
            (typical winning scores at PGA events: -25 to +10). */}
        <div style={{
          background: 'rgba(8,14,22,0.45)',
          border: '1px solid rgba(255,255,255,0.10)',
          borderRadius: 12,
          padding: '12px 14px',
          marginBottom: 14,
        }}>
          <label style={{ display: 'block', fontSize: 12, color: 'var(--text-dim)', marginBottom: 6, fontFamily: 'Arial, sans-serif' }}>
            Tiebreaker · Winning score to par (e.g. <strong>-15</strong>)
          </label>
          <input
            type="text"
            inputMode="numeric"
            value={tieGuess}
            onChange={e => {
              const v = e.target.value
              // Allow empty, lone "-", lone "+", or signed integer
              if (v === '' || v === '-' || v === '+' || /^[+-]?\d{1,3}$/.test(v)) setTieGuess(v)
            }}
            disabled={submitted}
            placeholder="-15"
            style={{
              width: '100%',
              background: 'var(--color-card)',
              border: '1px solid var(--color-border)',
              borderRadius: 8,
              color: 'var(--text)',
              padding: '10px 14px',
              fontSize: 15,
              fontFamily: 'inherit',
              outline: 'none',
            }}
          />
          {tieGuess !== '' && !tieGuessValid && (
            <div style={{ fontSize: 11, color: 'var(--red)', marginTop: 4 }}>
              Enter an integer (-99 to 99)
            </div>
          )}
        </div>

        {/* Six tier sections, one per snapshot tier. Each section is a
            radio-style group: one selection per tier, tap to pick. */}
        {['1','2','3','4','5','6'].map(tierKey => {
          const dgIds   = Array.isArray(tierSnapshot[tierKey]) ? tierSnapshot[tierKey] : []
          const tierNum = parseInt(tierKey, 10)
          const myPick  = picks[tierKey]?.selectedDgId || null

          return (
            <div key={tierKey} style={{ marginBottom: 16 }}>
              {/* Tier header chip */}
              <div style={{
                background: 'rgba(8,14,22,0.65)',
                backdropFilter: 'blur(14px)',
                WebkitBackdropFilter: 'blur(14px)',
                border: '1px solid rgba(255,255,255,0.10)',
                borderRadius: 12,
                padding: '8px 12px',
                marginBottom: 8,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)', fontFamily: 'inherit' }}>
                  {tierLabels[tierKey]}
                </div>
                <div style={{
                  fontSize: 10, fontFamily: 'Arial, sans-serif', color: 'var(--text-muted)',
                  textTransform: 'uppercase', letterSpacing: 0.5,
                }}>
                  {myPick
                    ? <span style={{ color: 'var(--green)', fontWeight: 700 }}>✓ Picked</span>
                    : `${dgIds.length} players`}
                </div>
              </div>

              {/* Vertical list of golfers — tap to select */}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 6,
                maxHeight: 320,
                overflowY: 'auto',
                paddingRight: 4,
              }}>
                {dgIds.map(dgId => {
                  const dgKey  = String(dgId)
                  const player = tierPlayerLookup[dgKey] || {}
                  const name   = player.name || `dg_id ${dgKey}`
                  const winProbPct = (typeof player.winProb === 'number')
                    ? (player.winProb * 100).toFixed(1) + '%'
                    : '—'
                  const isSelected = myPick === dgKey
                  return (
                    <button
                      key={dgKey}
                      type="button"
                      onClick={() => handlePick(tierNum, dgKey)}
                      disabled={submitted}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 8,
                        background: isSelected ? 'var(--gold-bg2)' : 'var(--color-card)',
                        border: isSelected
                          ? '2px solid var(--gold)'
                          : '1px solid var(--color-border)',
                        borderRadius: 10,
                        padding: '10px 12px',
                        cursor: submitted ? 'not-allowed' : 'pointer',
                        textAlign: 'left',
                        fontFamily: 'inherit',
                        opacity: submitted && !isSelected ? 0.5 : 1,
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                        {/* Country flag if available, else initials avatar */}
                        {player.country
                          ? <img
                              src={flagUrl(player.country)}
                              alt=""
                              style={{ width: 20, height: 14, objectFit: 'cover', borderRadius: 2, flexShrink: 0 }}
                            />
                          : <div style={{
                              width: 20, height: 14, borderRadius: 2, background: 'var(--surface2)',
                              fontSize: 8, color: 'var(--text-dim)', display: 'flex',
                              alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                            }}>{playerInitials(name)}</div>
                        }
                        <span style={{
                          fontSize: 13,
                          color: isSelected ? 'var(--gold)' : 'var(--text)',
                          fontWeight: isSelected ? 700 : 500,
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}>
                          {name}
                        </span>
                      </div>
                      <span style={{
                        fontSize: 11,
                        fontFamily: 'Arial, sans-serif',
                        color: isSelected ? 'var(--gold)' : 'var(--text-dim)',
                        flexShrink: 0,
                      }}>
                        {winProbPct}
                      </span>
                    </button>
                  )
                })}
              </div>
            </div>
          )
        })}

        {/* Submitted state banner — visible when entry is locked */}
        {submitted && (
          <div style={{
            background: 'var(--success-bg)',
            border: '1px solid var(--success-border)',
            borderRadius: 10,
            padding: '10px 14px',
            marginTop: 8,
            fontSize: 13,
            color: 'var(--success-fg)',
            textAlign: 'center',
            fontFamily: 'inherit',
          }}>
            ✓ Entry locked in — 6 picks submitted{tieGuessInt != null ? `, tiebreaker ${tieGuessInt}` : ''}
          </div>
        )}

      </div>{/* end content wrapper (z:1) */}

      {/* Sticky-submit — unwrapped sibling, root-context z:90. Same
          pattern as GolfPicksMode's submit (keeps it above .bottom-nav
          z:50, below modals z:100+). Hidden when `locked` is true; the
          sticky unlock bar (below) replaces it as the bottom CTA so the
          user always has exactly one sticky bottom element. */}
      {!locked && canSubmit && (
        <button
          type="button"
          onClick={handleSubmit}
          className="btn btn-gold"
          style={{
            position: 'fixed',
            bottom: 80,
            left: '50%',
            transform: 'translateX(-50%)',
            width: 'auto',
            minWidth: 220,
            padding: '12px 28px',
            fontSize: 14,
            fontWeight: 700,
            zIndex: 90,
            boxShadow: '0 4px 14px rgba(0,0,0,0.4)',
            fontFamily: 'inherit',
          }}
        >
          Submit 6-Pick Entry
        </button>
      )}

      {/* Pattern #2 sticky unlock bar — visible whenever the pool is
          locked, regardless of pick count. Replaces the sticky-submit
          (which is suppressed above when locked=true). Triggers the
          unlock checkout via onUnlockRequest; renders the same root-
          context z:90 as the sticky-submit so it sits above bottom-nav
          (z:50) and below modals (z:100+). */}
      {locked && (
        <div
          style={{
            position: 'fixed',
            bottom: 80,
            left: 16,
            right: 16,
            maxWidth: 500,
            margin: '0 auto',
            background: 'var(--surface2)',
            border: '1px solid var(--gold)',
            borderRadius: 12,
            padding: '12px 14px',
            zIndex: 90,
            boxShadow: '0 4px 14px rgba(0,0,0,0.4)',
            fontFamily: 'inherit',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
          }}
        >
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)' }}>
              🔒 Pool is locked
            </div>
            <div style={{
              fontSize: 11, color: 'var(--text-dim)', marginTop: 2,
              fontFamily: 'Arial, sans-serif',
            }}>
              Unlock to save your picks
            </div>
          </div>
          <button
            type="button"
            onClick={onUnlockRequest}
            disabled={unlocking}
            className="btn btn-gold"
            style={{
              whiteSpace: 'nowrap',
              padding: '8px 14px',
              fontSize: 13,
              fontWeight: 700,
              opacity: unlocking ? 0.6 : 1,
              cursor: unlocking ? 'wait' : 'pointer',
              fontFamily: 'inherit',
            }}
          >
            {unlocking
              ? 'Unlocking…'
              : (unlockPriceCents != null
                  ? `Unlock $${(unlockPriceCents / 100).toFixed(2)}`
                  : 'Unlock')}
          </button>
        </div>
      )}

      {/* Toast — unwrapped sibling, root-context z:100. */}
      {toast && (
        <div style={{
          position: 'fixed', bottom: 140, left: '50%', transform: 'translateX(-50%)',
          background: 'var(--surface2)', border: '1px solid var(--border)',
          borderRadius: 8, padding: '10px 16px', fontSize: 12, color: 'var(--text)',
          zIndex: 100, boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
          fontFamily: 'inherit', maxWidth: '90vw',
        }}>
          {toast}
        </div>
      )}
    </div>
  )
}
